/**
 * useSupervisorTurn — the SSE write-path hook.
 *
 * POSTs to the supervisor endpoint, then manually pumps a `ReadableStream`
 * reader loop, feeding decoded chunks through `advisorySseParser.ts`'s
 * frame parser and routing frames into hook state.
 *
 *   - Unmount must not abort the in-flight fetch. Aborting on unmount
 *     cancels the agent-svc write that creates
 *     `users/{uid}/strategic_advisory/{id}` (that write happens during the
 *     stream, before the LLM returns). React 18 StrictMode and Fast Refresh
 *     remount this hook in development, so aborting on unmount was silently
 *     killing every first turn. The pump loop double-checks
 *     `controller.signal.aborted` around `reader.read()` — once in the loop
 *     condition before the read, and once again right after — so a chunk
 *     that resolves at the exact instant abort() fires is discarded rather
 *     than decoded or dispatched.
 *   - A second `send()` call aborts whatever turn is currently in flight,
 *     unconditionally, before starting the new one. Turns are strictly
 *     sequential — a second `send()` means the user acted again, and the
 *     first turn's narration is worthless. A changed identityKey also aborts
 *     the previous user's turn; unmount must not abort.
 *   - A clean stream end (`done: true`) with no explicit SSE `done` event
 *     resolves to `status: 'done'`, not an error. A non-OK HTTP response,
 *     or a 200 response with no body at all, IS an error: both are
 *     protocol violations, not a clean end. This only concerns stream
 *     termination — `goal`/`goalConfirmed`/`engagement`/`questions` SSE
 *     content is read and stored here as real data (see the cases below),
 *     not discarded as display-only narration.
 *   - Unknown event names and unparseable `data:` payloads are skipped,
 *     never fatal — a parse or routing throw must never kill the reader
 *     loop. The documented SSE `error` event is surfaced, but only through
 *     the mapped i18n key, never as raw provider text (ASVS V7).
 *
 * The reader-loop and fetch mechanics were written fresh, informed only by
 * reading an earlier reference implementation via `git show` — that branch
 * was never checked out and no identifier, import, filename or fixture
 * from it appears here. Two of its behaviors are deliberately not
 * reproduced: its `start()` returned a `cancel()` handle that the calling
 * component simply discarded (here, `send()` itself aborts overlapping
 * turns, so callers still never need a cancel handle; unmount deliberately
 * does not abort — see above), and its reader loop checked only `done`,
 * never an aborted flag, so a chunk resolving after `abort()` was still
 * processed (this hook's loop double-checks `aborted` before and after
 * every read).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
// Type-only: the analysis turn accumulator is this hook's one functional
// state update, and `safeSet`'s generic has to be told that explicitly.
import type { SetStateAction } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../redux/store';
import { getValidToken } from '../../redux/slices/userSlice';
import {
  ADVISORY_SUPERVISOR_ENDPOINT,
  ADVISORY_SCHEMA_VERSION,
  type AdvisorySupervisorRequestField,
  type AdvisorySupervisorRequestBody,
} from '../../redux/slices/advisory/advisoryConfig';
import {
  ADVISORY_SUPERVISOR_ERROR_CODE,
  mapAdvisoryErrorCode,
} from '../../redux/slices/advisory/advisoryErrorCodes';
import {
  parseWorkspaceSnapshot,
  parseWorkspaceResult,
  type WorkspaceRequest,
  type WorkspaceSnapshot,
  type WorkspaceResult,
} from './advisoryWorkspace';
import { createAdvisorySseFrameParser } from './advisorySseParser';
import {
  parseReviewSnapshot,
  parseReviewConfirmed,
  parseReviewProgress,
  type ReviewProgress,
  type ReviewRequest,
  type ReviewSnapshot,
  type ReviewConfirmed,
} from './advisoryReview';
import { TERMINAL_COUNTERPARTY_PROFILE_STATUSES } from '../../redux/slices/advisory/advisoryTypes';
import type {
  CounterpartyProfileStatus,
  LiveCounterpartyProfiles,
} from '../../redux/slices/advisory/advisoryTypes';
import {
  reduceSupervisorAnswerEvent,
  type AnswersRerunMode,
  type SupervisorAnswerRunState,
} from './advisoryAnswerRun';
import {
  reportingPeriodExactKeys,
  validateReportingPeriodV01,
  type ReportingPeriodV01,
} from './advisoryReportingPeriod';

export type { AnswersRerunMode, SupervisorAnswerRunState } from './advisoryAnswerRun';
export type { ReportingPeriodV01 } from './advisoryReportingPeriod';

export type SupervisorTurnStatus = 'idle' | 'sending' | 'streaming' | 'done' | 'error';

export interface SupervisorTurnRequest {
  workspace?: WorkspaceRequest;
  review?: ReviewRequest;
  projectId: string;
  userQuery?: string;
  refinement?: string;
  confirm?: boolean;
  /**
   * Re-runs classification against an already-confirmed goal, after a
   * classification failure. Already present in the request DTO's field
   * allowlist (`ADVISORY_SUPERVISOR_REQUEST_FIELDS`, `advisoryConfig.ts`) —
   * this interface field and the body wiring below are the only changes
   * needed to send it (see the module header's own comment on the request
   * body).
   */
  classify?: boolean;
  /**
   * Removes one or more key questions server-side. The live security rules
   * deny every client write on `strategic_advisory/**` by design —
   * `order`/`dependsOn` are a server-owned topological sort a client write
   * could corrupt — so removal goes through this same request/response
   * turn, never a Firestore `deleteDoc`. `removeQuestions` is already on
   * `ADVISORY_SUPERVISOR_REQUEST_FIELDS`'s allowlist.
   */
  removeQuestions?: string[];
  /**
   * Proposes a user-authored key question. The server matches it against
   * the engagement catalog and responds with a `questionProposals` SSE
   * event carrying the closest matches. Nothing is stored until the user
   * picks one via `acceptQuestion`. `addQuestion` is already on
   * `ADVISORY_SUPERVISOR_REQUEST_FIELDS`'s allowlist.
   */
  addQuestion?: string;
  /**
   * Accepts one of the catalog candidates the server proposed in response
   * to a prior `addQuestion` turn. The server adds it to the project and
   * responds with an updated `questions` SSE event. `acceptQuestion` is
   * already on `ADVISORY_SUPERVISOR_REQUEST_FIELDS`'s allowlist.
   */
  acceptQuestion?: string;
  /**
   * Locks the question set (S-04b). This is the gate the analysis stage waits
   * on: until it is sent, `startAnalysis` is refused. Once locked, every edit
   * turn — `removeQuestions`, `addQuestion`, `acceptQuestion` — is refused
   * server-side, so a run cannot have its questions changed underneath it.
   *
   * Refused on an empty set (a project the user emptied is not a finished plan),
   * and idempotent once locked, so a double click reports the lock rather than
   * failing.
   */
  confirmQuestions?: boolean;
  /**
   * Settles which company the engagement is about (S-02a). The name itself was
   * already read from the user's opening question on the first turn (S-02a) and
   * written to `facts/company` unconfirmed; this is what marks it confirmed.
   *
   * Accepted from every company state, including a failed extraction — a model
   * that would not answer is no reason to hold an engagement hostage when the
   * user knows the name perfectly well. Gated on the goal being confirmed, NOT
   * on the question set being locked: this client locks the set at the very end,
   * bundled with `startAnalysis`.
   */
  confirmCompany?: boolean;
  /**
   * The name to settle on, when the user types over the proposal. Omitted means
   * "accept what was proposed". Only read alongside `confirmCompany` — on its
   * own it does nothing, so the subject of a project cannot be changed without
   * going through the confirmation step.
   */
  companyName?: string;
  /** Confirms the reporting period detected on the opening turn (S-02b). */
  confirmPeriod?: boolean;
  /** Omitted to accept the stored proposal; supplied to persist a correction. */
  period?: ReportingPeriodV01;
  /** Explicitly selects the server's current calendar year without a period payload. */
  useCurrentYear?: boolean;
  /**
   * Settles the competitors, or the acquisition targets, this engagement is
   * carried out against (S-03a). Only meaningful for the two engagement types
   * that have them; refused for the other nineteen.
   */
  confirmCounterparties?: boolean;
  /**
   * The WHOLE curated list, not a diff — the user adds, deletes and accepts in
   * one screen, and one array write cannot half-apply the way a delete plus an
   * add can. Only read alongside `confirmCounterparties`.
   */
  counterparties?: string[];
  /**
   * Retries ONE counterparty's researched profile, optionally starting from a
   * URL the user supplies because the first attempt found nothing.
   *
   * Cheap where it can be: a profile whose research succeeded and whose
   * INGESTION failed is re-ingested from the stored prose server-side rather
   * than researched again.
   */
  retryCounterpartyProfile?: boolean;
  /** Which company to retry. Must be one of the confirmed counterparties. */
  counterparty?: string;
  /** Where the research should start. Optional — a retry without one asks again. */
  counterpartyUrl?: string;
  /**
   * Mints the V3 conversation id and returns both docintel collection handles
   * without running anything (S-05, first half). That is the document-upload
   * window: a handle is `uuid5(namespace, conversationId + suffix)`, so nothing
   * can be uploaded until the id exists — and a node reads the collection as it
   * stands when its turn runs, so a file arriving mid-run is a file the
   * engagement never sees.
   *
   * Idempotent and never re-minting: an existing conversation id is kept,
   * because re-minting would leave every already-uploaded document in a
   * collection nothing reads.
   */
  prepareAnalysis?: boolean;
  /**
   * Runs the locked question set through V3 (S-05, second half) — one reflection
   * per question, chained per subquestion.
   *
   * The stream lasts the length of the engagement: roughly 13 minutes on the
   * default 2x2 debug shape, hours on a full one. Losing the stream does not
   * lose the run — each V3 turn is a detached Cloud Run Job — and re-sending
   * this re-attaches to the turns already posted rather than starting a second
   * engagement.
   */
  startAnalysis?: boolean;
  /** Starts or resumes S-06 answering through the same supervisor endpoint. */
  startAnswers?: boolean;
  /** UI automation uses `resume`; the other literals remain part of the closed backend DTO. */
  answersRerunMode?: AnswersRerunMode;
  /**
   * Per-run overrides for the analysis shape. Each falls back to agent-svc's own
   * `SUPERVISOR_ANALYSIS_*` setting when omitted, which is why every one is
   * optional and why the request body below omits them rather than defaulting
   * them: a default here would pin the shape of a run nobody asked for.
   *
   * `analysisDebug: false` runs the WHOLE question set. On a large engagement
   * that is hours of wall clock and millions of tokens.
   */
  analysisDebug?: boolean;
  analysisMaxKeyQuestions?: number;
  analysisMaxSubquestions?: number;
  /**
   * `simon` is graph retrieval only; `simon-docintel` also runs the document
   * agent on the opening and closing nodes, which is the only thing that reads
   * files the user uploaded — and costs 420-600s per turn instead of 170-200s.
   * A closed set on both agent-svc and gmid: an unrecognised name would fall
   * back to the configured default and run a whole engagement under a
   * configuration nobody chose.
   */
  analysisNodeProfile?: 'simon' | 'simon-docintel';
}

/**
 * The settled outcome of exactly one `send()` call, resolved from that
 * call's OWN local state at the point it terminates — never re-derived by
 * a caller reading this hook's reactive `status`/`errorI18nKey` after the
 * fact, which would race a second overlapping `send()` or simply read a
 * stale value before React has re-rendered. `status: 'idle'` here means
 * "this specific call was superseded/aborted before it settled" — distinct
 * from the hook's own idle initial state, and never a state a caller
 * should treat as success OR failure.
 */
export interface SupervisorTurnOutcome {
  workspace?: WorkspaceSnapshot;
  workspaceResult?: WorkspaceResult;
  workspaceFailure?: 'rejected' | 'uncertain';
  review?: ReviewSnapshot;
  reviewConfirmed?: ReviewConfirmed;
  /** Only review failures: rejected requests can be corrected; uncertain writes must be retried. */
  reviewFailure?: 'rejected' | 'uncertain';
  status: SupervisorTurnStatus;
  errorI18nKey: string | null;
}

/**
 * The `goal` SSE event's parsed payload. Field names verified against
 * `agent-svc origin/development@afffb5d`,
 * `app/features/supervisor/service.py`'s module docstring — `goal`, never
 * `text`.
 *
 * Deliberately carries NO `canRefine` field, even though the wire payload
 * itself has one — `mapGoalDoc`/`mapEngagementSummaryDoc`
 * (`advisoryService.ts`) already established the pattern this hook matches:
 * derive `iteration < maxIterations` at the point of use rather than trust a
 * boolean the transport happens to carry, so a hardcoded or stale cap can
 * never survive a refactor.
 */
export interface SupervisorGoalState {
  goal: string;
  rationale: string;
  clarificationNeeded: boolean;
  clarificationQuestion: string;
  iteration: number;
  maxIterations: number;
}

/** The `goalConfirmed` SSE event's parsed payload — `{goal, iteration}` only, per source. */
export interface SupervisorGoalConfirmedState {
  goal: string;
  iteration: number;
}

/** One entry of the `engagement` SSE event's `alternatives` array. */
export interface SupervisorEngagementAlternative {
  engagementId: string;
  name: string;
  reason: string;
}

/** The `engagement` SSE event's parsed payload. */
export interface SupervisorEngagementState {
  engagementId: string;
  name: string;
  description: string;
  confidence: string;
  rationale: string;
  alternatives: SupervisorEngagementAlternative[];
}

/**
 * One entry of the `questions` SSE event's `questions` array. Field names —
 * `question`/`subquestions` (lowercase q) — verified against the same
 * `agent-svc` source as `SupervisorGoalState` above; do not reuse
 * `KeyQuestionDoc`, which models the Firestore-subcollection-document shape
 * (a different, narrower set of fields) rather than this wire event.
 */
export interface SupervisorKeyQuestion {
  id: string;
  question: string;
  component: string;
  subquestions: string[];
  order: number;
  dependsOn: string[];
  per: string;
  when: unknown[];
  source: string;
  catalogEngagements: string[];
  matchedFromUserQuestion: string;
}

/**
 * The `questionsRemoved` SSE event's own `orphaned` field — a map from a
 * still-present key question's `id` (present in this same turn's
 * `questions` response) to the list of just-removed id(s) it used to
 * `dependsOn`. Field names verified against the real recorded payload
 * (`advisorySupervisorFixtures.ts`'s `removeOne`/`removeAll`/`removeRepeat`
 * turns, `turn4-remove-one.txt` etc.) — never a raw id rendered to the user;
 * `AdvisoryKeyQuestionEditor` resolves each key against its own
 * `keyQuestions` prop for the question text before display.
 */
export type SupervisorOrphanedMap = Record<string, string[]>;

/** One entry of the `questionProposals` SSE event's `proposals` array. */
export interface SupervisorProposal {
  id: string;
  question: string;
  component: string;
  reason: string;
}

/**
 * The `questionProposals` SSE event's parsed payload. Arrives in response to
 * an `addQuestion` turn — the server matched the user's free-text question
 * against the engagement catalog and returns the closest candidates. Nothing
 * is stored until the user picks one via `acceptQuestion`.
 */
export interface SupervisorProposalState {
  userQuery: string;
  poorMatch: boolean;
  proposals: SupervisorProposal[];
}

/**
 * Compile-time assertion: makes an out-of-allowlist field on
 * `SupervisorTurnRequest` itself a compile error, not just on the body
 * literal below. `keyof SupervisorTurnRequest` must be assignable to
 * `AdvisorySupervisorRequestField` (`advisoryConfig.ts`, the DTO's real
 * field list); if a future edit adds a field the DTO does not accept, the
 * conditional type below evaluates to `never` and assigning `true` to it
 * fails to compile. No runtime behaviour — `void` below exists solely so no
 * unused-variable lint rule fires. Mirrors `advisoryTypes.typecheck.ts`'s
 * existing compile-time-assertion convention, kept here (not in a
 * `.typecheck.ts` file) because this module is reachable from the
 * production build, the only compile that runs reliably in CI.
 */
type SupervisorTurnRequestKeysAreAllowed =
  keyof SupervisorTurnRequest extends AdvisorySupervisorRequestField ? true : never;
const supervisorTurnRequestKeysAreAllowed: SupervisorTurnRequestKeysAreAllowed = true;
void supervisorTurnRequestKeysAreAllowed;

/**
 * What `confirmQuestions` locked (S-04b).
 *
 * `alreadyConfirmed` distinguishes a fresh lock from an idempotent no-op on a set
 * already locked, so a double click — or a run started from a set the user locked
 * by hand earlier — reports the lock rather than looking like a new one.
 *
 * `count` is written server-side only at lock time. While a set is open the
 * backend deliberately stores no count, because it would be wrong from the first
 * edit and a stale count looks authoritative.
 */
export interface SupervisorQuestionsConfirmedState {
  count: number;
  alreadyConfirmed: boolean;
}

/**
 * The company the engagement is about, as the stream reported it.
 *
 * Emitted twice on different turns and they mean different things, which is
 * what `needsConfirmation` distinguishes: on the first turn (S-02a) it is a
 * read, delivered alongside the goal, and nothing is being asked; when the
 * question set is approved (S-04c) it is the question itself.
 *
 * `companyName` is empty when extraction found nothing. That is still a
 * question, not an answer — the user names it themselves.
 */
export interface SupervisorCompanyState {
  companyName: string;
  /** Whether the UI must put this to the user before letting them continue. */
  needsConfirmation: boolean;
  /** True once settled. */
  confirmed: boolean;
  /** The read itself failed, as opposed to finding nobody — different copy. */
  extractionFailed: boolean;
}

/** What `confirmCompany` settled. */
export interface SupervisorCompanyConfirmedState {
  companyName: string;
}

/** The strict `period` proposal emitted alongside company extraction. */
export interface SupervisorPeriodState {
  period: ReportingPeriodV01;
  confirmed: boolean;
  needsConfirmation: boolean;
  defaulted: boolean;
  extractionFailed: boolean;
}

/** The settled period and its canonical server-owned fact path. */
export interface SupervisorPeriodConfirmedState {
  period: ReportingPeriodV01;
  factPath: string;
}

/**
 * The OTHER companies, as the stream reported them.
 *
 * Emitted on the confirm turn that classifies the engagement, and only for the
 * two per-counterparty types. `kind` is the server's word for what they are, so
 * the UI does not have to map an engagement id to a noun.
 */
export interface SupervisorCounterpartiesState {
  companies: string[];
  kind: string;
  confirmed: boolean;
  needsConfirmation: boolean;
  extractionFailed: boolean;
}

/** What `confirmCounterparties` settled. */
export interface SupervisorCounterpartiesConfirmedState {
  companies: string[];
  kind: string;
}

/**
 * Live profile-research progress, accumulated across a confirm or retry turn.
 *
 * Accumulating rather than last-event-wins: the stream reports one company at a
 * time and the screen shows all of them, so a per-event replacement would blank
 * every row but the one that just moved.
 *
 * Live counterparty-research progress for this turn.
 *
 * Aliases the shared `LiveCounterpartyProfiles` because the STAGE consumes it:
 * `facts/counterparties` is read once at mount, before any profile exists, so
 * without this overlay the research screen could never appear during a run.
 */
export type SupervisorCounterpartyProfilesState = LiveCounterpartyProfiles;

/**
 * Untrusted transport data: a status the client does not know is DISCARDED
 * rather than stored, because an unrecognised value renders as neither
 * in-flight nor finished — the one state the profile screen cannot show.
 *
 * `researching` is accepted here even though it is not terminal: agent-svc
 * writes it to Firestore before a job starts, and echoing it costs nothing.
 */
function isLiveProfileStatus(value: unknown): value is CounterpartyProfileStatus {
  return (
    typeof value === 'string' &&
    (value === 'researching' ||
      (TERMINAL_COUNTERPARTY_PROFILE_STATUSES as readonly string[]).includes(value))
  );
}

/** The empty overlay. One definition: five inline copies is how a new field
 * gets missed on four of them. */
function emptyProfiles(): SupervisorCounterpartyProfilesState {
  return { kind: '', companies: [], started: [], settled: {}, dropped: [], completed: null };
}

/**
 * What `prepareAnalysis` published (S-05, first half).
 *
 * Both collection handles are derived from `conversationId`, which is why this
 * turn exists at all: a document can be uploaded into `docsCollectionId` only
 * once the id it derives from is known, and only before the run starts.
 * `alreadyPrepared` distinguishes a fresh mint from an idempotent no-op, so a
 * second click reports the existing conversation rather than looking like a
 * new one.
 */
export interface SupervisorAnalysisPreparedState {
  conversationId: string;
  /** Where the user's uploaded documents go. The only handle a client writes to. */
  docsCollectionId: string;
  /** Where pgms ingests every completed node's output. Reported, never written to. */
  internalCollectionId: string;
  alreadyPrepared: boolean;
}

/**
 * One turn of the engagement, as the stream reported it.
 *
 * `failed` is a BOOLEAN and there is deliberately no message field. The wire
 * payload carries an `error` string straight from pgms — raw provider text — and
 * this hook never lets that reach renderable state, the same rule the `error`
 * SSE event follows (ASVS V7). Whether a turn failed is renderable; why it did
 * belongs in the tracking record a developer reads.
 *
 * `blankSlots` is the one number worth watching. `totalEdges` says what
 * retrieval DECIDED; `blankSlots` says what the LLM actually RECEIVED, and for
 * 184 V3 executions those disagreed completely — edge counts and scores reported
 * success while every injected memory slot was empty.
 */
export interface SupervisorAnalysisTurn {
  label: string;
  phase: string;
  kind: string;
  keyQuestionId: string;
  /** 1-based position within its chain; `null` for a root or key-question turn. */
  subquestionN: number | null;
  turnId: string;
  parentTurnId: string;
  status: string;
  failed: boolean;
  wallSeconds: number | null;
  totalTokens: number;
  totalEdges: number;
  blankSlots: number;
}

/**
 * The engagement run, accumulated across the whole `startAnalysis` stream.
 *
 * Unlike every other slot on this hook, this one ACCUMULATES rather than
 * replacing: a 7-turn run emits roughly thirty turn transitions, and a
 * single-value slot would render whichever happened to arrive last instead of a
 * progress tree. `turns` therefore keeps one entry per label, in first-seen
 * order, with each later transition replacing that label's entry.
 *
 * `runId` is the pointer into `strategic_advisory/{projectId}/tracking/{runId}`,
 * where the per-turn node read-outs live. `trackingPath` is the full path, named
 * on the wire so a client does not reconstruct it.
 */
export interface SupervisorAnalysisState {
  runId: string;
  conversationId: string;
  trackingPath: string;
  /** True when this call re-attached to a run already in flight rather than starting one. */
  resumed: boolean;
  plannedTurns: number;
  /** Turns on the longest non-overlapping chain — what the wall clock actually costs. */
  criticalPathTurns: number;
  /** The complete run shape emitted before any turn starts. */
  plan?: Record<string, unknown>;
  nodeProfile: string;
  /** `''` while in flight; otherwise the run's terminal status. */
  status: string;
  /** True once a terminal `analysisFailed` arrived. The message is never stored. */
  failed: boolean;
  completedTurns: number;
  failedTurns: number;
  totalTokens: number;
  totalEdges: number;
  /** Non-zero means retrieval reported success and delivered nothing. */
  totalBlankSlots: number;
  /** Reported beside `totalEdges`: over zero edges a scope check passes vacuously. */
  outOfLineageTurns: number;
  /** Key questions whose chain stopped early. Their remaining subquestions never ran. */
  brokenChains: string[];
  turns: SupervisorAnalysisTurn[];
}

export interface UseSupervisorTurnResult {
  review: ReviewSnapshot | null;
  reviewProgress: ReviewProgress | null;
  reviewConfirmed: ReviewConfirmed | null;
  status: SupervisorTurnStatus;
  /** Display-only narration from the `status` SSE event. Never persisted. */
  progressPhase: string | null;
  /** Always a mapped i18n key — NEVER raw provider text (ASVS V7). */
  errorI18nKey: string | null;
  /**
   * The current turn's parsed `goal` SSE event, or `null` before one has
   * arrived (or after a later `send()` reset it — see `send`'s own
   * comment). This is hook-local state, not Redux state: it is turn-scoped
   * narration, not durable state, and is never dispatched to Redux.
   */
  goal: SupervisorGoalState | null;
  /** The current turn's parsed `goalConfirmed` SSE event, or `null`. */
  goalConfirmed: SupervisorGoalConfirmedState | null;
  /** The current turn's parsed `engagement` SSE event, or `null`. */
  engagement: SupervisorEngagementState | null;
  /** The current turn's parsed `questions` SSE event's array, or `[]`. */
  questions: SupervisorKeyQuestion[];
  /**
   * The current turn's parsed `questionsRemoved` SSE event's `orphaned`
   * map, or `{}` before one has arrived (or after a later `send()` resets
   * it — same turn-scoped-narration reasoning as `goal`/`engagement`/
   * `questions` above). `{}` on every turn that is not a removal, and on a
   * removal that orphaned nothing (the `removeAll` and `removeRepeat`
   * fixtures both carry `orphaned: {}`).
   */
  orphaned: SupervisorOrphanedMap;
  /**
   * The current turn's parsed `questionProposals` SSE event, or `null` before
   * one has arrived. Persists across an `acceptQuestion` turn (so the editor
   * can keep showing the candidates while the accept is in flight and retry
   * on failure); cleared by any non-accept `send()`, and by a `questionAdded`
   * event confirming the accept landed.
   */
  proposals: SupervisorProposalState | null;
  /** The current turn's parsed `questionsConfirmed` SSE event, or `null`. */
  questionsConfirmed: SupervisorQuestionsConfirmedState | null;
  /**
   * The current turn's parsed `company` SSE event, or `null`. Arrives on the
   * first turn as a read (`needsConfirmation: false`, alongside the goal) and
   * again when the question set is approved as the question itself.
   */
  company: SupervisorCompanyState | null;
  /** The current turn's parsed `companyConfirmed` SSE event, or `null`. */
  companyConfirmed: SupervisorCompanyConfirmedState | null;
  /** The current turn's parsed reporting-period proposal, or `null`. */
  period: SupervisorPeriodState | null;
  /** The current turn's parsed reporting-period confirmation, or `null`. */
  periodConfirmed: SupervisorPeriodConfirmedState | null;
  /** The current turn's parsed `counterparties` SSE event, or `null`. */
  counterparties: SupervisorCounterpartiesState | null;
  /** The current turn's parsed `counterpartiesConfirmed` SSE event, or `null`. */
  counterpartiesConfirmed: SupervisorCounterpartiesConfirmedState | null;
  /** Live profile-research progress for this turn, or `null` before it starts. */
  counterpartyProfiles: SupervisorCounterpartyProfilesState | null;
  /**
   * The current turn's parsed `analysisPrepared` SSE event, or `null`. Carries
   * the handle a document must be uploaded into BEFORE `startAnalysis` runs.
   */
  analysisPrepared: SupervisorAnalysisPreparedState | null;
  /**
   * The engagement run, accumulated across a `startAnalysis` stream, or `null`
   * before one has started. The only accumulating slot on this hook — see
   * `SupervisorAnalysisState`.
   */
  analysis: SupervisorAnalysisState | null;
  /** Transient S-06 progress. Firestore remains authoritative for completion and content. */
  answers: SupervisorAnswerRunState | null;
  /**
   * Resolves with THIS call's own settled outcome (`SupervisorTurnOutcome`),
   * not `void` — a caller that needs to know whether its own specific
   * request (e.g. a removal) succeeded can await this directly instead of
   * racing the hook's reactive `status` state across re-renders. Every
   * pre-existing fire-and-forget call site (`void send(request)`) is
   * unaffected — it already discarded the resolved value.
   */
  send: (request: SupervisorTurnRequest) => Promise<SupervisorTurnOutcome>;
}

const SUPERVISOR_ERROR_I18N_KEY = mapAdvisoryErrorCode(ADVISORY_SUPERVISOR_ERROR_CODE).i18nKey;

export interface UseSupervisorTurnOptions {
  /** Local ownership boundary only; never forwarded to the supervisor. */
  identityKey?: string;
}

export function useSupervisorTurn({
  identityKey,
}: UseSupervisorTurnOptions = {}): UseSupervisorTurnResult {
  // Uses `useDispatch<AppDispatch>()` directly from `react-redux`, with
  // `AppDispatch` imported as a type only — NOT `useAppDispatch()` from
  // `redux/store.ts`. That wrapper is a value import of the whole store
  // module, which runs `configureStore`/`persistStore`/
  // `startAdvisorySessionWatcher(store)` as import-time side effects
  // (`store.ts:29-50`). A CT/Vite harness that mounts a component using this
  // hook has no `jest.mock` equivalent to intercept that, so the side effect
  // reaches the (stubbed) Firebase `auth` singleton and throws
  // (`e2e/fixtures/firebaseConfigStub.ts`'s `inert()`), which also breaks
  // every other screen sharing the same Vite chunk. This matches the
  // dominant pattern elsewhere in the codebase — `AdvisoryEngagementPage.tsx`,
  // `AdvisoryListPage.tsx` and `EngagementRow.tsx` all call
  // `useDispatch<AppDispatch>()` this same way, and `useSupervisorTurn.test.ts`
  // already has to `jest.mock` around the exact same coupling (see that
  // file's own comment). No behavior change: `useDispatch()` resolves via
  // React context, not the module-level singleton.
  const dispatch = useDispatch<AppDispatch>();
  const abortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  // A fresh object also invalidates an A -> B -> A switch. Update during render
  // so previous-user data is hidden before effects and late frames cannot land
  // between the identity render and the abort effect.
  const identityRef = useRef({ key: identityKey });
  if (identityRef.current.key !== identityKey) {
    identityRef.current = { key: identityKey };
  }
  const identity = identityRef.current;
  const stateIdentityRef = useRef(identity);

  const [status, setStatusState] = useState<SupervisorTurnStatus>('idle');
  const [progressPhase, setProgressPhaseState] = useState<string | null>(null);
  const [errorI18nKey, setErrorI18nKeyState] = useState<string | null>(null);
  const [reviewProgress, setReviewProgressState] = useState<ReviewProgress | null>(null);
  const [review, setReviewState] = useState<ReviewSnapshot | null>(null);
  const [reviewConfirmed, setReviewConfirmedState] = useState<ReviewConfirmed | null>(null);
  const [goal, setGoalState] = useState<SupervisorGoalState | null>(null);
  const [goalConfirmed, setGoalConfirmedState] = useState<SupervisorGoalConfirmedState | null>(
    null
  );
  const [engagement, setEngagementState] = useState<SupervisorEngagementState | null>(null);
  const [questions, setQuestionsState] = useState<SupervisorKeyQuestion[]>([]);
  const [orphaned, setOrphanedState] = useState<SupervisorOrphanedMap>({});
  const [proposals, setProposalsState] = useState<SupervisorProposalState | null>(null);
  const [company, setCompanyState] = useState<SupervisorCompanyState | null>(null);
  const [companyConfirmed, setCompanyConfirmedState] =
    useState<SupervisorCompanyConfirmedState | null>(null);
  const [period, setPeriodState] = useState<SupervisorPeriodState | null>(null);
  const [periodConfirmed, setPeriodConfirmedState] =
    useState<SupervisorPeriodConfirmedState | null>(null);
  const [counterparties, setCounterpartiesState] = useState<SupervisorCounterpartiesState | null>(
    null
  );
  const [counterpartiesConfirmed, setCounterpartiesConfirmedState] =
    useState<SupervisorCounterpartiesConfirmedState | null>(null);
  const [counterpartyProfiles, setCounterpartyProfilesState] =
    useState<SupervisorCounterpartyProfilesState | null>(null);
  const [questionsConfirmed, setQuestionsConfirmedState] =
    useState<SupervisorQuestionsConfirmedState | null>(null);
  const [analysisPrepared, setAnalysisPreparedState] =
    useState<SupervisorAnalysisPreparedState | null>(null);
  const [analysis, setAnalysisState] = useState<SupervisorAnalysisState | null>(null);
  const [answers, setAnswersState] = useState<SupervisorAnswerRunState | null>(null);

  // safeSet: every state write in this hook goes through this — no bare
  // setter call anywhere in an async path. No-ops once the component using
  // this hook has unmounted, so a late-resolving chunk cannot cause a React
  // "update on unmounted component" warning even if it somehow reached this
  // far (it should never get this far; the aborted checks in the pump loop
  // below are the primary guard).
  const safeSet = useCallback(function safeSet<T>(setter: (value: T) => void, value: T): void {
    if (isMountedRef.current) {
      setter(value);
    }
  }, []);

  // Unmounting flips the mount ref so safeSet no-ops; it deliberately does
  // NOT abort the in-flight controller (see module header) — only an
  // overlapping send() or identity change aborts.
  useEffect(() => {
    // Re-arm on every mount, not just the first. React 18 StrictMode
    // double-invokes effects in development (mount → cleanup → mount) against
    // the same fiber, so a cleanup-only version of this effect leaves the ref
    // stuck at `false` after the second mount and silently swallows every
    // later setState — `safeSet` above is the single path EVERY state write in
    // this hook takes, so the whole hook would go mute under `npm run dev`:
    // status never leaves 'idle', so the editors' in-flight line, their Add
    // gating and the inline failure-plus-retry never appear. Identical to the
    // guard `AdvisoryKeyQuestionEditor.tsx` already carries, for identical
    // reasons.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (stateIdentityRef.current !== identity) {
      abortRef.current?.abort();
      safeSet(setReviewState, null);
      safeSet(setReviewProgressState, null);
      safeSet(setReviewConfirmedState, null);
      safeSet(setStatusState, 'idle');
      safeSet(setProgressPhaseState, null);
      safeSet(setErrorI18nKeyState, null);
      safeSet(setGoalState, null);
      safeSet(setGoalConfirmedState, null);
      safeSet(setEngagementState, null);
      safeSet(setQuestionsState, []);
      safeSet(setOrphanedState, {});
      safeSet(setProposalsState, null);
      safeSet(setQuestionsConfirmedState, null);
      safeSet(setCompanyState, null);
      safeSet(setCompanyConfirmedState, null);
      safeSet(setPeriodState, null);
      safeSet(setPeriodConfirmedState, null);
      safeSet(setCounterpartiesState, null);
      safeSet(setCounterpartiesConfirmedState, null);
      safeSet(setCounterpartyProfilesState, null);
      safeSet(setAnalysisPreparedState, null);
      safeSet(setAnalysisState, null);
      safeSet(setAnswersState, null);
      stateIdentityRef.current = identity;
    }
  }, [identity, safeSet]);

  const send = useCallback(
    async (request: SupervisorTurnRequest): Promise<SupervisorTurnOutcome> => {
      // A retained callback from the previous identity must not start a request
      // or abort work that already belongs to the new signed-in user.
      if (identityRef.current !== identity) {
        return { status: 'idle', errorI18nKey: null };
      }
      // Abort whatever turn is currently in flight, unconditionally, before
      // starting the new one. Capture the fresh controller in a LOCAL const
      // and check that local const for the rest of this call — never
      // abortRef.current — so a later overlapping send() replacing the ref
      // cannot make THIS turn think it is still current.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const changedIdentity = stateIdentityRef.current !== identity;
      stateIdentityRef.current = identity;
      const isCurrent = () => identityRef.current === identity && !controller.signal.aborted;

      let receivedWorkspace: WorkspaceSnapshot | undefined;
      let receivedWorkspaceResult: WorkspaceResult | undefined;
      let receivedReview: ReviewSnapshot | undefined;
      let progressOrder = -1;
      let receivedApproval: ReviewConfirmed | undefined;
      let hasRejectionSnapshot = false;
      let reviewMayHaveStarted = false;
      const reviewOutcome = (
        outcome: SupervisorTurnOutcome,
        failure: SupervisorTurnOutcome['reviewFailure'] = 'uncertain'
      ): SupervisorTurnOutcome =>
        !isCurrent()
          ? { status: 'idle', errorI18nKey: null }
          : request.workspace
          ? {
              ...outcome,
              workspace: receivedWorkspace,
              workspaceResult: receivedWorkspaceResult,
              ...(receivedWorkspaceResult && !receivedWorkspaceResult.matching
                ? {}
                : {
                    workspaceFailure: failure,
                  }),
            }
          : request.review
          ? {
              ...outcome,
              review: receivedReview,
              reviewConfirmed: receivedApproval,
              ...(outcome.status === 'error' ? { reviewFailure: failure } : {}),
            }
          : outcome;
      safeSet(setReviewState, null);
      safeSet(setReviewProgressState, null);
      safeSet(setReviewConfirmedState, null);
      safeSet(setProgressPhaseState, null);
      safeSet(setErrorI18nKeyState, null);
      // A new turn starts with a blank SSE-state canvas, for the same
      // reason as progressPhase/errorI18nKey above — a prior turn's
      // narration is worthless once superseded. A confirm turn does not
      // itself re-emit a 'goal' event (only 'goalConfirmed'), so this also
      // correctly clears a stale goal-review payload once the turn that
      // would have rendered it is no longer the current one.
      safeSet(setGoalState, null);
      safeSet(setGoalConfirmedState, null);
      safeSet(setEngagementState, null);
      safeSet(setQuestionsState, []);
      // Same reasoning as questions/goal/engagement above — a prior turn's
      // orphaned-dependency narration is worthless once a new turn
      // (removal or otherwise) supersedes it.
      safeSet(setOrphanedState, {});
      // Same rule. The durable answer lives on the project document's `company`
      // slot and in `facts/company`; this slot is only what THIS turn said.
      safeSet(setCompanyState, null);
      safeSet(setCompanyConfirmedState, null);
      safeSet(setPeriodState, null);
      safeSet(setPeriodConfirmedState, null);
      safeSet(setCounterpartiesState, null);
      safeSet(setCounterpartiesConfirmedState, null);
      safeSet(setCounterpartyProfilesState, null);
      // Proposals persist through an accept turn so the editor can keep
      // showing the candidates (for retry on failure); every other turn
      // clears them.
      if (changedIdentity || !request.acceptQuestion) {
        safeSet(setProposalsState, null);
      }
      // Both analysis slots clear on every turn, for the same reason as the
      // rest: a prior turn's narration is worthless once superseded. That is
      // correct even for a re-attach — `startAnalysis` against a run already in
      // flight re-emits `analysisStarted` and then a transition for every turn
      // it finds, so the tree rebuilds from the stream rather than merging into
      // a stale one.
      safeSet(setQuestionsConfirmedState, null);
      safeSet(setAnalysisPreparedState, null);
      safeSet(setAnalysisState, null);
      safeSet(setAnswersState, null);
      safeSet(setStatusState, 'sending');

      try {
        if (
          (request.review || request.workspace) &&
          Object.entries(request).some(
            ([key, value]) =>
              key !== 'projectId' &&
              key !== (request.workspace ? 'workspace' : 'review') &&
              value != null &&
              value !== false &&
              value !== '' &&
              !(Array.isArray(value) && value.length === 0)
          )
        ) {
          safeSet(setErrorI18nKeyState, SUPERVISOR_ERROR_I18N_KEY);
          safeSet(setStatusState, 'error');
          return reviewOutcome(
            { status: 'error', errorI18nKey: SUPERVISOR_ERROR_I18N_KEY },
            'rejected'
          );
        }
        if (request.useCurrentYear && (request.confirmPeriod || request.period !== undefined)) {
          safeSet(setErrorI18nKeyState, SUPERVISOR_ERROR_I18N_KEY);
          safeSet(setStatusState, 'error');
          return reviewOutcome(
            { status: 'error', errorI18nKey: SUPERVISOR_ERROR_I18N_KEY },
            'rejected'
          );
        }
        const token = await dispatch(getValidToken()).unwrap();
        if (!isCurrent()) {
          return { status: 'idle', errorI18nKey: null };
        }

        // The body's key set is constrained by `AdvisorySupervisorRequestBody`
        // (`advisoryConfig.ts`), so a field outside
        // `ADVISORY_SUPERVISOR_REQUEST_FIELDS` — the DTO's real field list —
        // is a compile error (`TS2353`) rather than a field the DTO silently
        // discards under `extra="ignore"`. The explicit type annotation
        // below is load-bearing: without it an added key would be an
        // inferred widening, not an excess-property error. Never a
        // wholesale spread of `request` — that is what keeps
        // userId/conversationId off this wire (see the NEVER comment
        // below). The current fields (`classify`, `removeQuestions`) are
        // already on the allowlist; wiring a new one in here needs only an
        // interface field, no config change and no gate edit.
        const body: AdvisorySupervisorRequestBody = {
          projectId: request.projectId,
          review: request.review,
          workspace: request.workspace,
          userQuery: request.userQuery ?? '',
          refinement: request.refinement ?? '',
          confirm: request.confirm ?? false,
          // A classification retry against an already-confirmed goal.
          // Always sent (defaulted false), matching this body's existing
          // convention of never conditionally omitting a field.
          classify: request.classify ?? false,
          // A key-question removal turn. Defaulted to `[]` rather than
          // omitted, matching this body's own convention above.
          removeQuestions: request.removeQuestions ?? [],
          addQuestion: request.addQuestion ?? '',
          // The S-04b lock. Always sent (defaulted false), same convention as
          // `confirm`/`classify` above — it is a turn selector, not an override.
          confirmQuestions: request.confirmQuestions ?? false,
          acceptQuestion: request.acceptQuestion ?? '',
          // The two S-05 turn selectors, defaulted false for the same reason.
          confirmCompany: request.confirmCompany ?? false,
          confirmCounterparties: request.confirmCounterparties ?? false,
          counterparties: request.counterparties ?? [],
          retryCounterpartyProfile: request.retryCounterpartyProfile ?? false,
          counterparty: request.counterparty ?? '',
          counterpartyUrl: request.counterpartyUrl ?? '',
          // Sent as-is. Empty means "accept the proposal"; the server reads it
          // only alongside `confirmCompany`.
          companyName: request.companyName ?? '',
          confirmPeriod: request.useCurrentYear ? undefined : request.confirmPeriod ?? false,
          period: request.period,
          // Opt-in: the server chooses the year; an absent action stays absent.
          useCurrentYear: request.useCurrentYear,
          prepareAnalysis: request.prepareAnalysis ?? false,
          startAnalysis: request.startAnalysis ?? false,
          startAnswers: request.startAnswers ?? false,
          answersRerunMode: request.answersRerunMode,
          // The four run-shape overrides are the ONE deliberate departure from
          // this body's never-conditionally-omit convention, and the departure
          // is the point: agent-svc reads an absent override as "use the
          // configured default", so defaulting one here would have this client
          // choose the shape of a run the caller said nothing about — a
          // `analysisDebug: false` sent by accident is hours of wall clock and
          // millions of tokens. `undefined` is dropped by `JSON.stringify`, and
          // gmid's `exclude_none=True` drops it again, so an omitted override
          // never reaches agent-svc as a null it would have to interpret.
          analysisDebug: request.analysisDebug,
          analysisMaxKeyQuestions: request.analysisMaxKeyQuestions,
          analysisMaxSubquestions: request.analysisMaxSubquestions,
          analysisNodeProfile: request.analysisNodeProfile,
          schemaVersion: ADVISORY_SCHEMA_VERSION,
        };

        const response = await fetch(ADVISORY_SUPERVISOR_ENDPOINT, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: `Bearer ${token}`,
          },
          // NEVER userId: gmid injects it from the validated Firebase JWT.
          // NEVER conversationId: the supervisor endpoint has no such field.
          body: JSON.stringify(body),
        });

        if (!isCurrent()) {
          // Superseded by a newer send() before the response even arrived —
          // this call's own outcome is inert (see `SupervisorTurnOutcome`'s
          // own doc comment).
          return { status: 'idle', errorI18nKey: null };
        }

        // A non-OK HTTP response is an error. Never call response.text()
        // and never place any part of the upstream body into state — it is
        // untrusted provider text (ASVS V7).
        if (!response.ok) {
          safeSet(setErrorI18nKeyState, SUPERVISOR_ERROR_I18N_KEY);
          safeSet(setStatusState, 'error');
          return reviewOutcome(
            { status: 'error', errorI18nKey: SUPERVISOR_ERROR_I18N_KEY },
            response.status === 422 ? 'rejected' : 'uncertain'
          );
        }

        const reader = response.body?.getReader();
        if (!reader) {
          // A 200 with no stream body is a protocol violation, not a clean
          // end — that distinction only applies to a stream that ended, not
          // a stream that never existed.
          safeSet(setErrorI18nKeyState, SUPERVISOR_ERROR_I18N_KEY);
          safeSet(setStatusState, 'error');
          return reviewOutcome({ status: 'error', errorI18nKey: SUPERVISOR_ERROR_I18N_KEY });
        }

        safeSet(setStatusState, 'streaming');
        const decode = new TextDecoder();
        const parseChunk = createAdvisorySseFrameParser();

        // Never `for await...of` here — Safari lacks
        // ReadableStream[Symbol.asyncIterator] and browserslist targets
        // Safari. The `!controller.signal.aborted` loop condition is the
        // BEFORE check; the check immediately following `await
        // reader.read()` is the AFTER check: a chunk can already be
        // resolved and in flight at the instant abort() fires, and the
        // resolved value must be discarded rather than decoded or routed.
        // Set by the 'error' and 'done' frame cases below. Both are terminal:
        // once either arrives the turn is over, so the pump must stop rather
        // than keep reading until the stream happens to close. Without this,
        // a natural close after an 'error' frame reached the `done` branch
        // below and silently overwrote status 'error' with 'done'.
        let terminalFrameSeen = false;
        // Set alongside `terminalFrameSeen` by the 'error'/'done' cases below
        // — this call's own settled outcome, read back after the loop exits.
        let terminalOutcome: SupervisorTurnOutcome = { status: 'done', errorI18nKey: null };
        let terminalFailure: SupervisorTurnOutcome['reviewFailure'] = 'uncertain';

        while (isCurrent()) {
          const { value, done } = await reader.read();

          if (!isCurrent()) {
            return { status: 'idle', errorI18nKey: null };
          }

          if (done) {
            // A clean stream end WITHOUT an explicit 'done' SSE event is
            // success, not an error — Firestore, not SSE, is the source of
            // truth for real completion.
            safeSet(setStatusState, 'done');
            return reviewOutcome({ status: 'done', errorI18nKey: null });
          }

          try {
            parseChunk(decode.decode(value, { stream: true }), frame => {
              if (!isCurrent() || terminalFrameSeen) return;
              switch (frame.event) {
                case 'status': {
                  let phase: string = frame.data;
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (
                      parsed &&
                      typeof parsed === 'object' &&
                      typeof (parsed as { phase?: unknown }).phase === 'string'
                    ) {
                      phase = (parsed as { phase: string }).phase;
                    }
                  } catch {
                    // An unparseable status payload falls back to the raw
                    // string rather than throwing.
                  }
                  safeSet(setProgressPhaseState, phase);
                  break;
                }
                case 'error':
                  // Never read `message` out of the payload. agent-svc
                  // emits { "message": str(exc) } — a raw Python exception
                  // string — and raw provider text must never reach
                  // renderable state (ASVS V7).
                  safeSet(setErrorI18nKeyState, SUPERVISOR_ERROR_I18N_KEY);
                  safeSet(setStatusState, 'error');
                  terminalFrameSeen = true;
                  terminalOutcome = { status: 'error', errorI18nKey: SUPERVISOR_ERROR_I18N_KEY };
                  // Workspace refusals emit a snapshot followed by an explicit
                  // error frame. A read failure after a snapshot is uncertain:
                  // successful commands emit their snapshot before the receipt.
                  if (request.workspace && receivedWorkspace) {
                    terminalFailure = 'rejected';
                  }
                  // The backend increments update/refine revisions atomically
                  // before starting work. Only this call's settled, unchanged
                  // snapshot can prove the mutation was rejected. Raw exception
                  // text and snapshots from another read channel prove nothing.
                  if (
                    (request.review?.operation === 'update' ||
                      request.review?.operation === 'refine') &&
                    hasRejectionSnapshot &&
                    !reviewMayHaveStarted &&
                    !receivedApproval
                  ) {
                    terminalFailure = 'rejected';
                  }
                  break;
                case 'reviewProgress': {
                  try {
                    const parsed =
                      request.review &&
                      parseReviewProgress(
                        JSON.parse(frame.data),
                        request.review,
                        request.projectId
                      );
                    const order = parsed
                      ? ['preparing', 'understandingDetails', 'shapingEngagement'].indexOf(
                          parsed.stage
                        )
                      : -1;
                    if (
                      parsed &&
                      order >= progressOrder &&
                      (!receivedReview ||
                        (receivedReview.revision === parsed.revision &&
                          receivedReview.status === 'preparing'))
                    ) {
                      progressOrder = order;
                      safeSet(setReviewProgressState, parsed);
                    }
                  } catch {
                    // Malformed and future progress stages are optional narration.
                  }
                  break;
                }
                case 'workspace': {
                  try {
                    receivedWorkspace =
                      parseWorkspaceSnapshot(JSON.parse(frame.data), request.projectId) ??
                      undefined;
                  } catch {
                    /* Invalid snapshots are never rendered. */
                  }
                  break;
                }
                case 'workspaceResult': {
                  try {
                    if (request.workspace)
                      receivedWorkspaceResult =
                        parseWorkspaceResult(JSON.parse(frame.data), request.workspace) ??
                        undefined;
                  } catch {
                    /* No canonical result: retry the same request. */
                  }
                  break;
                }
                case 'review': {
                  hasRejectionSnapshot = false;
                  try {
                    const parsed = parseReviewSnapshot(JSON.parse(frame.data), request.projectId);
                    if (parsed) {
                      receivedReview = parsed;
                      safeSet(setReviewState, parsed);
                      const operation = request.review;
                      if (operation && operation.operation !== 'prepare') {
                        hasRejectionSnapshot =
                          parsed.revision === operation.expectedRevision &&
                          (parsed.status === 'ready' || parsed.status === 'needsAttention') &&
                          parsed.approvedAt === null;
                        reviewMayHaveStarted ||=
                          parsed.revision > operation.expectedRevision ||
                          parsed.status === 'preparing' ||
                          parsed.status === 'approved' ||
                          parsed.approvedAt !== null;
                      }
                    }
                  } catch {
                    /* Invalid review frames never become renderable state. */
                  }
                  break;
                }
                case 'reviewConfirmed': {
                  try {
                    const parsed = parseReviewConfirmed(JSON.parse(frame.data), request.projectId);
                    if (parsed) reviewMayHaveStarted = true;
                    if (
                      parsed &&
                      request.review?.operation === 'confirm' &&
                      parsed.requestId === request.review.requestId &&
                      parsed.revision === request.review.expectedRevision
                    ) {
                      receivedApproval = parsed;
                      safeSet(setReviewConfirmedState, parsed);
                    }
                  } catch {
                    /* Skip malformed or unrelated receipts. */
                  }
                  break;
                }
                case 'goal': {
                  // Goal content arrives over this SSE event, not only via
                  // the Firestore listener. An unparseable or non-object
                  // payload is skipped, never fatal, mirroring the 'status'
                  // case's own tolerance above. Every field is read
                  // defensively (typeof-guarded, defaulted) rather than
                  // cast, since this payload is untrusted transport data.
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      safeSet(setGoalState, {
                        goal: typeof p.goal === 'string' ? p.goal : '',
                        rationale: typeof p.rationale === 'string' ? p.rationale : '',
                        clarificationNeeded:
                          typeof p.clarificationNeeded === 'boolean'
                            ? p.clarificationNeeded
                            : false,
                        clarificationQuestion:
                          typeof p.clarificationQuestion === 'string'
                            ? p.clarificationQuestion
                            : '',
                        iteration: typeof p.iteration === 'number' ? p.iteration : 0,
                        maxIterations: typeof p.maxIterations === 'number' ? p.maxIterations : 0,
                      });
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'goalConfirmed': {
                  // Same as 'goal' above: this event's content is stored,
                  // not treated as Firestore-only display narration.
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      safeSet(setGoalConfirmedState, {
                        goal: typeof p.goal === 'string' ? p.goal : '',
                        iteration: typeof p.iteration === 'number' ? p.iteration : 0,
                      });
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'engagement': {
                  // Classification's real result, captured rather than
                  // discarded. `description`/`rationale`
                  // and each alternative's `reason` are agent-authored text —
                  // stored as plain strings only, never interpreted as HTML
                  // by any consumer of this state (ASVS V7).
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      const rawAlternatives = Array.isArray(p.alternatives) ? p.alternatives : [];
                      const alternatives: SupervisorEngagementAlternative[] = rawAlternatives
                        .filter(
                          (a): a is Record<string, unknown> => a !== null && typeof a === 'object'
                        )
                        .map(a => ({
                          engagementId: typeof a.engagementId === 'string' ? a.engagementId : '',
                          name: typeof a.name === 'string' ? a.name : '',
                          reason: typeof a.reason === 'string' ? a.reason : '',
                        }));
                      safeSet(setEngagementState, {
                        engagementId: typeof p.engagementId === 'string' ? p.engagementId : '',
                        name: typeof p.name === 'string' ? p.name : '',
                        description: typeof p.description === 'string' ? p.description : '',
                        confidence: typeof p.confidence === 'string' ? p.confidence : '',
                        rationale: typeof p.rationale === 'string' ? p.rationale : '',
                        alternatives,
                      });
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'questions': {
                  // Captured in full (all eleven documented fields),
                  // including `order`/`component`/`subquestions` — this
                  // hook stores the whole wire shape even though rendering
                  // only uses server `order` or `component`. Storing less
                  // here would just mean a downstream consumer has to
                  // re-add the fields this hook already saw on the wire and
                  // threw away.
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      const rawQuestions = Array.isArray(p.questions) ? p.questions : [];
                      const mapped: SupervisorKeyQuestion[] = rawQuestions
                        .filter(
                          (q): q is Record<string, unknown> => q !== null && typeof q === 'object'
                        )
                        .map(q => ({
                          id: typeof q.id === 'string' ? q.id : '',
                          question: typeof q.question === 'string' ? q.question : '',
                          component: typeof q.component === 'string' ? q.component : '',
                          subquestions: Array.isArray(q.subquestions)
                            ? (q.subquestions as string[])
                            : [],
                          order: typeof q.order === 'number' ? q.order : 0,
                          dependsOn: Array.isArray(q.dependsOn) ? (q.dependsOn as string[]) : [],
                          per: typeof q.per === 'string' ? q.per : '',
                          when: Array.isArray(q.when) ? q.when : [],
                          source: typeof q.source === 'string' ? q.source : '',
                          catalogEngagements: Array.isArray(q.catalogEngagements)
                            ? (q.catalogEngagements as string[])
                            : [],
                          matchedFromUserQuestion:
                            typeof q.matchedFromUserQuestion === 'string'
                              ? q.matchedFromUserQuestion
                              : '',
                        }));
                      safeSet(setQuestionsState, mapped);
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'questionsRemoved': {
                  // The removal turn's own outcome payload —
                  // `{removed, unknown, orphaned}` per the recorded
                  // fixtures. Only `orphaned` is captured; `removed`/`unknown`
                  // have no display need yet (the removed card simply stops
                  // rendering once the very next `questions` event, always
                  // sent alongside this one, lands). Every value is read
                  // defensively, matching every other case's own tolerance
                  // for an untrusted transport payload.
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      const rawOrphaned =
                        p.orphaned && typeof p.orphaned === 'object'
                          ? (p.orphaned as Record<string, unknown>)
                          : {};
                      const mapped: SupervisorOrphanedMap = {};
                      for (const [id, deps] of Object.entries(rawOrphaned)) {
                        mapped[id] = Array.isArray(deps)
                          ? deps.filter((d): d is string => typeof d === 'string')
                          : [];
                      }
                      safeSet(setOrphanedState, mapped);
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'questionProposals': {
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      const rawProposals = Array.isArray(p.proposals) ? p.proposals : [];
                      const mapped: SupervisorProposal[] = rawProposals
                        .filter(
                          (pr): pr is Record<string, unknown> =>
                            pr !== null && typeof pr === 'object'
                        )
                        .map(pr => ({
                          id: typeof pr.id === 'string' ? pr.id : '',
                          question: typeof pr.question === 'string' ? pr.question : '',
                          component: typeof pr.component === 'string' ? pr.component : '',
                          reason: typeof pr.reason === 'string' ? pr.reason : '',
                        }));
                      safeSet(setProposalsState, {
                        userQuery: typeof p.userQuery === 'string' ? p.userQuery : '',
                        poorMatch: typeof p.poorMatch === 'boolean' ? p.poorMatch : false,
                        proposals: mapped,
                      });
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'questionAdded': {
                  safeSet(setProposalsState, null);
                  break;
                }
                case 'questionsConfirmed': {
                  // The S-04b lock's outcome. Emitted on both paths — a fresh
                  // lock and an idempotent one on a set already locked.
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      safeSet(setQuestionsConfirmedState, {
                        count: typeof p.count === 'number' ? p.count : 0,
                        alreadyConfirmed:
                          typeof p.alreadyConfirmed === 'boolean' ? p.alreadyConfirmed : false,
                      });
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'company': {
                  // S-02a's read and S-04c's question arrive on the same event
                  // name; `needsConfirmation` is what tells them apart.
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      safeSet(setCompanyState, {
                        companyName: typeof p.companyName === 'string' ? p.companyName : '',
                        needsConfirmation:
                          typeof p.needsConfirmation === 'boolean' ? p.needsConfirmation : false,
                        confirmed: typeof p.confirmed === 'boolean' ? p.confirmed : false,
                        extractionFailed:
                          typeof p.extractionFailed === 'boolean' ? p.extractionFailed : false,
                      });
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'companyConfirmed': {
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      safeSet(setCompanyConfirmedState, {
                        companyName: typeof p.companyName === 'string' ? p.companyName : '',
                      });
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'period': {
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                      const payload = parsed as Record<string, unknown>;
                      const mapped = validateReportingPeriodV01(payload.period);
                      if (
                        reportingPeriodExactKeys(payload, [
                          'period',
                          'confirmed',
                          'needsConfirmation',
                          'defaulted',
                          'extractionFailed',
                        ]) &&
                        mapped.ok &&
                        typeof payload.confirmed === 'boolean' &&
                        typeof payload.needsConfirmation === 'boolean' &&
                        typeof payload.defaulted === 'boolean' &&
                        typeof payload.extractionFailed === 'boolean'
                      ) {
                        safeSet(setPeriodState, {
                          period: mapped.value,
                          confirmed: payload.confirmed,
                          needsConfirmation: payload.needsConfirmation,
                          defaulted: payload.defaulted,
                          extractionFailed: payload.extractionFailed,
                        });
                      }
                    }
                  } catch {
                    // Malformed period narration is skipped, never fatal.
                  }
                  break;
                }
                case 'periodConfirmed': {
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                      const payload = parsed as Record<string, unknown>;
                      const mapped = validateReportingPeriodV01(payload.period);
                      if (
                        reportingPeriodExactKeys(payload, ['period', 'factPath']) &&
                        mapped.ok &&
                        typeof payload.factPath === 'string'
                      ) {
                        safeSet(setPeriodConfirmedState, {
                          period: mapped.value,
                          factPath: payload.factPath,
                        });
                      }
                    }
                  } catch {
                    // Malformed period confirmation is skipped, never fatal.
                  }
                  break;
                }
                case 'counterparties': {
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      const list = Array.isArray(p.companies) ? p.companies : [];
                      safeSet(setCounterpartiesState, {
                        companies: list.filter((c): c is string => typeof c === 'string'),
                        kind: typeof p.kind === 'string' ? p.kind : '',
                        confirmed: typeof p.confirmed === 'boolean' ? p.confirmed : false,
                        needsConfirmation:
                          typeof p.needsConfirmation === 'boolean' ? p.needsConfirmation : false,
                        extractionFailed:
                          typeof p.extractionFailed === 'boolean' ? p.extractionFailed : false,
                      });
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'counterpartiesConfirmed': {
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      const list = Array.isArray(p.companies) ? p.companies : [];
                      safeSet(setCounterpartiesConfirmedState, {
                        companies: list.filter((c): c is string => typeof c === 'string'),
                        kind: typeof p.kind === 'string' ? p.kind : '',
                      });
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'counterpartyProfilesStarted': {
                  try {
                    const p = JSON.parse(frame.data) as Record<string, unknown>;
                    const list = Array.isArray(p.companies) ? p.companies : [];
                    safeSet(setCounterpartyProfilesState, {
                      kind: typeof p.kind === 'string' ? p.kind : '',
                      companies: list.filter((c): c is string => typeof c === 'string'),
                      started: [],
                      settled: {},
                      dropped: [],
                      completed: null,
                    });
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'counterpartyProfileStarted': {
                  // In flight. Written before the research begins, so the row
                  // shows work rather than a gap.
                  try {
                    const p = JSON.parse(frame.data) as Record<string, unknown>;
                    const company = typeof p.company === 'string' ? p.company : '';
                    if (!company) {
                      break;
                    }
                    setCounterpartyProfilesState(current => {
                      const base = current ?? emptyProfiles();
                      return {
                        ...base,
                        // A company can restart (a retry), so a stale terminal
                        // outcome is cleared — otherwise the row would show the
                        // previous failure while the new job runs.
                        settled: Object.fromEntries(
                          Object.entries(base.settled).filter(([c]) => c !== company)
                        ),
                        started: base.started.includes(company)
                          ? base.started
                          : [...base.started, company],
                      };
                    });
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'counterpartyProfileSettled': {
                  // TERMINAL, success or failure. agent-svc emits this from the
                  // single point every outcome is recorded at, which is what
                  // makes a failed company visible live — the retry it needs
                  // used to require a reload to appear.
                  try {
                    const p = JSON.parse(frame.data) as Record<string, unknown>;
                    const company = typeof p.company === 'string' ? p.company : '';
                    const status = p.status;
                    if (!company || !isLiveProfileStatus(status)) {
                      break;
                    }
                    setCounterpartyProfilesState(current => {
                      const base = current ?? emptyProfiles();
                      return {
                        ...base,
                        started: base.started.filter(c => c !== company),
                        settled: {
                          ...base.settled,
                          [company]: {
                            status,
                            fileName: typeof p.fileName === 'string' ? p.fileName : '',
                            chars: typeof p.chars === 'number' ? p.chars : 0,
                            detail: typeof p.detail === 'string' ? p.detail : '',
                          },
                        },
                      };
                    });
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'counterpartyDropped': {
                  // A company given up on: the research failed, the user gave
                  // the URL they had, and that failed too. Accumulated like the
                  // other profile events, and ALSO removed from `started` — the
                  // drop is that company's terminal outcome, so leaving it in
                  // flight would hold a spinner that will never resolve.
                  try {
                    const p = JSON.parse(frame.data) as Record<string, unknown>;
                    const company = typeof p.company === 'string' ? p.company : '';
                    if (!company) {
                      break;
                    }
                    setCounterpartyProfilesState(current => {
                      const base = current ?? emptyProfiles();
                      return {
                        ...base,
                        started: base.started.filter(c => c !== company),
                        dropped: base.dropped.includes(company)
                          ? base.dropped
                          : [...base.dropped, company],
                      };
                    });
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'counterpartyProfilesCompleted': {
                  try {
                    const p = JSON.parse(frame.data) as Record<string, unknown>;
                    setCounterpartyProfilesState(current => ({
                      ...(current ?? emptyProfiles()),
                      started: [],
                      completed: {
                        planned: typeof p.planned === 'number' ? p.planned : 0,
                        ingested: typeof p.ingested === 'number' ? p.ingested : 0,
                      },
                    }));
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'analysisPrepared': {
                  // The document-upload window opening. Read defensively like
                  // every other case — this is untrusted transport data.
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      safeSet(setAnalysisPreparedState, {
                        conversationId:
                          typeof p.conversationId === 'string' ? p.conversationId : '',
                        docsCollectionId:
                          typeof p.docsCollectionId === 'string' ? p.docsCollectionId : '',
                        internalCollectionId:
                          typeof p.internalCollectionId === 'string' ? p.internalCollectionId : '',
                        alreadyPrepared:
                          typeof p.alreadyPrepared === 'boolean' ? p.alreadyPrepared : false,
                      });
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'analysisStarted': {
                  // Opens the run and carries the whole shape up front, so a
                  // progress tree renders from the first event as greyed-out rows
                  // that fill in, rather than appearing one completion at a time.
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (parsed && typeof parsed === 'object') {
                      const p = parsed as Record<string, unknown>;
                      const plan =
                        p.plan && typeof p.plan === 'object'
                          ? (p.plan as Record<string, unknown>)
                          : {};
                      const config =
                        p.config && typeof p.config === 'object'
                          ? (p.config as Record<string, unknown>)
                          : {};
                      safeSet(setAnalysisState, {
                        runId: typeof p.runId === 'string' ? p.runId : '',
                        conversationId:
                          typeof p.conversationId === 'string' ? p.conversationId : '',
                        trackingPath: typeof p.trackingPath === 'string' ? p.trackingPath : '',
                        resumed: typeof p.resumed === 'boolean' ? p.resumed : false,
                        plannedTurns: typeof plan.totalTurns === 'number' ? plan.totalTurns : 0,
                        criticalPathTurns:
                          typeof plan.criticalPathTurns === 'number' ? plan.criticalPathTurns : 0,
                        plan,
                        nodeProfile:
                          typeof config.nodeProfile === 'string' ? config.nodeProfile : '',
                        status: '',
                        failed: false,
                        completedTurns: 0,
                        failedTurns: 0,
                        totalTokens: 0,
                        totalEdges: 0,
                        totalBlankSlots: 0,
                        outOfLineageTurns: 0,
                        brokenChains: [],
                        turns: [],
                      });
                    }
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'analysisTurnAccepted':
                case 'analysisTurnReattached':
                case 'analysisTurnSkipped':
                case 'analysisTurnCompleted':
                case 'analysisTurnFailed': {
                  // The five turn transitions share one payload shape, so they
                  // share one handler; `status` on the record is what tells them
                  // apart afterwards.
                  //
                  // This is the hook's ONLY functional state update. A 7-turn run
                  // emits roughly thirty of these, and each has to be merged into
                  // the tree rather than replacing it — reading `analysis` from
                  // the enclosing closure would capture whatever value that
                  // render saw, so the last write would win and the tree would
                  // collapse to one row.
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (!parsed || typeof parsed !== 'object') {
                      break;
                    }
                    const p = parsed as Record<string, unknown>;
                    const label = typeof p.label === 'string' ? p.label : '';
                    if (!label) {
                      // The label is this record's identity — without one it
                      // cannot be placed in the tree or deduped against a later
                      // transition, so it is dropped rather than appended as a
                      // row that would grow on every poll.
                      break;
                    }
                    const record: SupervisorAnalysisTurn = {
                      label,
                      phase: typeof p.phase === 'string' ? p.phase : '',
                      kind: typeof p.kind === 'string' ? p.kind : '',
                      keyQuestionId: typeof p.keyQuestionId === 'string' ? p.keyQuestionId : '',
                      subquestionN: typeof p.subquestionN === 'number' ? p.subquestionN : null,
                      turnId: typeof p.turnId === 'string' ? p.turnId : '',
                      parentTurnId: typeof p.parentTurnId === 'string' ? p.parentTurnId : '',
                      status: typeof p.status === 'string' ? p.status : '',
                      // A boolean, never the message: `error` is raw pgms text and
                      // must not reach renderable state (ASVS V7).
                      failed: typeof p.error === 'string' && p.error.length > 0,
                      wallSeconds: typeof p.wallSeconds === 'number' ? p.wallSeconds : null,
                      totalTokens: typeof p.totalTokens === 'number' ? p.totalTokens : 0,
                      totalEdges: typeof p.totalEdges === 'number' ? p.totalEdges : 0,
                      blankSlots: typeof p.blankSlots === 'number' ? p.blankSlots : 0,
                    };
                    safeSet<SetStateAction<SupervisorAnalysisState | null>>(
                      setAnalysisState,
                      previous => {
                        if (previous === null) {
                          // A turn transition before `analysisStarted` is not
                          // expected, and inventing a run around it would report a
                          // shape nothing sent. Dropped.
                          return previous;
                        }
                        const index = previous.turns.findIndex(t => t.label === label);
                        const turns =
                          index === -1
                            ? [...previous.turns, record]
                            : previous.turns.map((t, i) => (i === index ? record : t));
                        return { ...previous, turns };
                      }
                    );
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'analysisChainBroken': {
                  // A chain stopped at a failed step. Its remaining subquestions
                  // never ran, which is deliberate server-side: they were written
                  // to build on the step that failed.
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (!parsed || typeof parsed !== 'object') {
                      break;
                    }
                    const p = parsed as Record<string, unknown>;
                    const keyQuestionId =
                      typeof p.keyQuestionId === 'string' ? p.keyQuestionId : '';
                    if (!keyQuestionId) {
                      break;
                    }
                    safeSet<SetStateAction<SupervisorAnalysisState | null>>(
                      setAnalysisState,
                      previous =>
                        previous === null || previous.brokenChains.includes(keyQuestionId)
                          ? previous
                          : { ...previous, brokenChains: [...previous.brokenChains, keyQuestionId] }
                    );
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'analysisRootCompleted':
                  // Phase 1 landed, so every branch may now be posted. Carries no
                  // field the tree does not already get from the root's own turn
                  // transition, so it is deliberately not stored — handled here
                  // only so it is not mistaken for an unhandled event.
                  break;
                case 'analysisCompleted':
                case 'analysisFailed': {
                  // The run's terminal frame. `status` distinguishes `completed`
                  // from `partial` — a run where one branch died still answered
                  // the others, and agent-svc reports that as `partial` rather
                  // than a failure precisely so the work is not thrown away.
                  try {
                    const parsed = JSON.parse(frame.data) as unknown;
                    if (!parsed || typeof parsed !== 'object') {
                      break;
                    }
                    const p = parsed as Record<string, unknown>;
                    const totals =
                      p.totals && typeof p.totals === 'object'
                        ? (p.totals as Record<string, unknown>)
                        : {};
                    const num = (value: unknown): number => (typeof value === 'number' ? value : 0);
                    safeSet<SetStateAction<SupervisorAnalysisState | null>>(
                      setAnalysisState,
                      previous =>
                        previous === null
                          ? previous
                          : {
                              ...previous,
                              status: typeof p.status === 'string' ? p.status : '',
                              // Boolean only — `error` is raw provider text.
                              failed: frame.event === 'analysisFailed',
                              completedTurns: num(p.completedTurns),
                              failedTurns: num(p.failedTurns),
                              plannedTurns: num(p.plannedTurns) || previous.plannedTurns,
                              totalTokens: num(totals.totalTokens),
                              totalEdges: num(totals.totalEdges),
                              totalBlankSlots: num(totals.totalBlankSlots),
                              outOfLineageTurns: num(totals.outOfLineageTurns),
                            }
                    );
                  } catch {
                    // Skipped, never fatal.
                  }
                  break;
                }
                case 'answersStarted':
                case 'answerTaskStarted':
                case 'answerTaskRetrying':
                case 'answerVerifying':
                case 'answerTaskCompleted':
                case 'answerTaskFailed':
                case 'answerTaskUnanswerable':
                case 'answerTaskSkipped':
                case 'answerProgress':
                case 'answersCompleted':
                case 'answersFailed': {
                  safeSet<SetStateAction<SupervisorAnswerRunState | null>>(
                    setAnswersState,
                    previous => reduceSupervisorAnswerEvent(previous, frame.event, frame.data)
                  );
                  break;
                }
                case 'done':
                  safeSet(setStatusState, 'done');
                  terminalFrameSeen = true;
                  terminalOutcome = { status: 'done', errorI18nKey: null };
                  break;
                default:
                  // Unknown event names are skipped, never fatal, never
                  // surfaced.
                  break;
              }
            });
          } catch (routingErr) {
            // A parse or routing throw must never kill the reader loop —
            // log and continue pumping.
            // eslint-disable-next-line no-console
            console.error('[useSupervisorTurn] frame routing error', routingErr);
          }

          if (terminalFrameSeen) {
            // Release the connection rather than leaving the pump parked on
            // a read that will never be consumed (this left a dangling open
            // handle). cancel() rejects if the stream is already closed, which
            // is not a failure worth surfacing.
            await reader.cancel().catch(() => undefined);
            return reviewOutcome(terminalOutcome, terminalFailure);
          }
        }
        // The while loop's own condition (`!controller.signal.aborted`)
        // became false without either inner aborted-check catching it first
        // — i.e. abort() fired at the exact instant between iterations. Same
        // inert outcome as the two aborted-checks above.
        return { status: 'idle', errorI18nKey: null };
      } catch (err) {
        const isAbort = !isCurrent() || (err instanceof Error && err.name === 'AbortError');
        if (isAbort) {
          // An aborted turn is not a user-facing failure — return silently.
          return { status: 'idle', errorI18nKey: null };
        }
        // Never place err.message into state.
        safeSet(setErrorI18nKeyState, SUPERVISOR_ERROR_I18N_KEY);
        safeSet(setStatusState, 'error');
        return reviewOutcome({ status: 'error', errorI18nKey: SUPERVISOR_ERROR_I18N_KEY });
      }
    },
    [dispatch, safeSet, identity]
  );

  const matchesIdentity = stateIdentityRef.current === identity;
  return {
    review: matchesIdentity ? review : null,
    reviewProgress:
      matchesIdentity && (status === 'sending' || status === 'streaming') ? reviewProgress : null,
    reviewConfirmed: matchesIdentity ? reviewConfirmed : null,
    status: matchesIdentity ? status : 'idle',
    progressPhase: matchesIdentity ? progressPhase : null,
    errorI18nKey: matchesIdentity ? errorI18nKey : null,
    goal: matchesIdentity ? goal : null,
    goalConfirmed: matchesIdentity ? goalConfirmed : null,
    engagement: matchesIdentity ? engagement : null,
    questions: matchesIdentity ? questions : [],
    orphaned: matchesIdentity ? orphaned : {},
    proposals: matchesIdentity ? proposals : null,
    questionsConfirmed: matchesIdentity ? questionsConfirmed : null,
    company: matchesIdentity ? company : null,
    companyConfirmed: matchesIdentity ? companyConfirmed : null,
    period: matchesIdentity ? period : null,
    periodConfirmed: matchesIdentity ? periodConfirmed : null,
    counterparties: matchesIdentity ? counterparties : null,
    counterpartiesConfirmed: matchesIdentity ? counterpartiesConfirmed : null,
    counterpartyProfiles: matchesIdentity ? counterpartyProfiles : null,
    analysisPrepared: matchesIdentity ? analysisPrepared : null,
    analysis: matchesIdentity ? analysis : null,
    answers: matchesIdentity ? answers : null,
    send,
  };
}
